import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, TrendingUp, Clock, CheckCircle, AlertTriangle, Wifi, WifiOff, ArrowRight, Check, Power, PowerOff, Smartphone, Monitor, Copy, Download, ExternalLink, ChevronDown, ChevronUp, Edit3 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import ParserConfigPanel from '../../components/admin/ParserConfigPanel';

export default function LiveRatesTab() {
  const [liveRates, setLiveRates] = useState(null);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [webhookHistory, setWebhookHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoSync, setAutoSync] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [scraperEnabled, setScraperEnabled] = useState(true);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [activeMethod, setActiveMethod] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('liveRatesMethod');
    return saved || 'manual';
  });
  const [showAndroidSetup, setShowAndroidSetup] = useState(false);
  const [showComputerSetup, setShowComputerSetup] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Manual entry state
  const [manualRates, setManualRates] = useState({ gold_22k: '', gold_18k: '', silver_999: '' });
  const [submittingManual, setSubmittingManual] = useState(false);
  
  // Save method choice to localStorage
  const handleMethodChange = (method) => {
    setActiveMethod(method);
    localStorage.setItem('liveRatesMethod', method);
    if (method === 'webhook') {
      setShowAndroidSetup(true);
      setShowHistory(true);
    }
  };

  const fetchLiveRates = useCallback(async () => {
    try {
      const [ratesRes, statusRes, settingsRes, webhookStatusRes, webhookHistoryRes, autoSyncRes] = await Promise.all([
        api.get('/kerala-bullion/live-rates'),
        api.get('/kerala-bullion/status'),
        api.get('/kerala-bullion/settings'),
        api.get('/kerala-bullion/webhook/status').catch(() => ({ data: { has_webhook_rates: false } })),
        api.get('/kerala-bullion/webhook/history').catch(() => ({ data: { history: [] } })),
        api.get('/kerala-bullion/auto-sync-setting').catch(() => ({ data: { enabled: false } }))
      ]);
      
      setLiveRates(ratesRes.data);
      setScrapeStatus(statusRes.data.scraper_status);
      setScraperEnabled(settingsRes.data.enabled);
      setWebhookStatus(webhookStatusRes.data);
      setWebhookHistory(webhookHistoryRes.data.history || []);
      setLastFetch(new Date());
      setAutoSync(autoSyncRes.data.enabled);
      
      // Auto-select webhook ONLY if user has never chosen a method (no localStorage key at all)
      const savedMethod = localStorage.getItem('liveRatesMethod');
      if (!savedMethod && webhookStatusRes.data?.has_webhook_rates) {
        setActiveMethod('webhook');
        setShowHistory(true);
        localStorage.setItem('liveRatesMethod', 'webhook');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveRates();
    const interval = setInterval(fetchLiveRates, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchLiveRates]);

  const handleToggleEnabled = async () => {
    setTogglingEnabled(true);
    try {
      const newState = !scraperEnabled;
      await api.put('/kerala-bullion/settings', { enabled: newState });
      setScraperEnabled(newState);
      toast.success(newState ? 'Server scraper enabled' : 'Server scraper disabled');
      if (newState) await fetchLiveRates();
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleForceRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/kerala-bullion/refresh');
      await fetchLiveRates();
      toast.success('Rates refreshed from Kerala Bullion');
      if (autoSync) await handleSyncToRates(true);
    } catch (error) {
      toast.error('Failed to refresh rates');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSyncToRates = async (silent = false) => {
    setSyncing(true);
    try {
      const res = await api.post('/kerala-bullion/sync-to-rates');
      if (!silent) {
        toast.success(`Synced ${res.data.synced_rates?.length || 0} rates to Metal Rates`);
      }
    } catch (error) {
      toast.error('Failed to sync rates');
    } finally {
      setSyncing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Manual rate submission
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    if (!manualRates.gold_22k) {
      toast.error('Please enter at least 22K gold rate');
      return;
    }
    
    setSubmittingManual(true);
    try {
      const rates = {
        gold_22k: parseFloat(manualRates.gold_22k),
        gold_18k: manualRates.gold_18k ? parseFloat(manualRates.gold_18k) : Math.round(parseFloat(manualRates.gold_22k) * 18 / 22),
        silver_999: manualRates.silver_999 ? parseFloat(manualRates.silver_999) : null,
        secret_key: secretKey
      };
      
      const response = await api.post('/kerala-bullion/webhook', rates);
      
      if (response.data.success) {
        toast.success('Rates updated successfully!');
        setManualRates({ gold_22k: '', gold_18k: '', silver_999: '' });
        fetchLiveRates();
      } else {
        toast.error(response.data.message || 'Failed to update rates');
      }
    } catch (error) {
      toast.error('Failed to update rates');
    } finally {
      setSubmittingManual(false);
    }
  };

  const formatRate = (rate) => {
    if (!rate) return '—';
    return `₹${rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'medium' });
  };

  const webhookUrl = `${window.location.origin}/api/kerala-bullion/webhook/notification`;
  const secretKey = 'marissa-gold-webhook-2024';

  const localComputerScript = `#!/usr/bin/env python3
"""
Kerala Bullion Auto Rate Pusher - Run on your local computer
Fetches rates every 30 minutes during trading hours (9:30 AM - 8:00 PM IST)

Setup:
  pip install playwright requests
  playwright install chromium
  python kerala_bullion_pusher.py
"""
import os, re, sys, time, asyncio, requests, logging
from datetime import datetime, timedelta

# Configuration
WEBHOOK_URL = "${window.location.origin}/api/kerala-bullion/webhook/notification"
WEBHOOK_SECRET = "${secretKey}"
MOBILE = "9789639358"
PASSWORD = "9358"
FETCH_INTERVAL = 30  # minutes

logging.basicConfig(level=logging.INFO, format='%(asctime)s - %(message)s')
logger = logging.getLogger(__name__)

def get_ist_time():
    return datetime.utcnow() + timedelta(hours=5, minutes=30)

def is_trading_hours():
    ist = get_ist_time()
    start = ist.replace(hour=9, minute=30, second=0)
    end = ist.replace(hour=20, minute=0, second=0)
    return start <= ist <= end

async def scrape_rates():
    from playwright.async_api import async_playwright
    rates_text = ""
    
    async with async_playwright() as p:
        browser = await p.chromium.launch(headless=True)
        page = await browser.new_page()
        
        try:
            logger.info("Loading keralabullion.com...")
            await page.goto('http://keralabullion.com/', timeout=60000)
            await page.wait_for_timeout(3000)
            
            # Login
            mobile_input = await page.query_selector('input[type="tel"], input[placeholder*="Mobile"]')
            if mobile_input:
                await mobile_input.fill(MOBILE)
            
            password_input = await page.query_selector('input[type="password"]')
            if password_input:
                await password_input.fill(PASSWORD)
            
            login_btn = await page.query_selector('button[type="submit"]')
            if login_btn:
                await login_btn.click()
                await page.wait_for_timeout(5000)
            
            # Get screen text
            rates_text = await page.evaluate('document.body.innerText')
            logger.info(f"Got {len(rates_text)} chars from screen")
            
        except Exception as e:
            logger.error(f"Scrape error: {e}")
        finally:
            await browser.close()
    
    return rates_text

def push_to_webhook(text):
    try:
        response = requests.post(
            WEBHOOK_URL,
            json={"notification_text": text, "secret_key": WEBHOOK_SECRET},
            timeout=30
        )
        if response.status_code == 200:
            data = response.json()
            if data.get('success'):
                rates = data.get('data', {})
                logger.info(f"✅ Pushed: 22K={rates.get('gold_22k')}, 18K={rates.get('gold_18k')}, Silver={rates.get('silver_999')}")
                return True
        logger.error(f"Push failed: {response.text}")
    except Exception as e:
        logger.error(f"Push error: {e}")
    return False

async def main():
    print("=" * 50)
    print("Kerala Bullion Rate Pusher")
    print(f"Webhook: {WEBHOOK_URL}")
    print("=" * 50)
    
    while True:
        if is_trading_hours():
            logger.info(f"IST: {get_ist_time().strftime('%I:%M %p')} - Fetching rates...")
            text = await scrape_rates()
            if text:
                push_to_webhook(text)
            logger.info(f"Next update in {FETCH_INTERVAL} minutes")
            await asyncio.sleep(FETCH_INTERVAL * 60)
        else:
            logger.info(f"Market closed. IST: {get_ist_time().strftime('%I:%M %p')}")
            await asyncio.sleep(3600)

if __name__ == "__main__":
    asyncio.run(main())
`;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#C9A967] border-t-transparent"></div>
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  const rates = liveRates?.data || {};
  const hasWebhookRates = webhookStatus?.has_webhook_rates;
  const isConnected = scrapeStatus?.status === 'success' || hasWebhookRates;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0a1628] to-[#1a3a5c] rounded-xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <TrendingUp className="h-7 w-7 text-[#C9A967]" />
              Metal Live Rate
            </h2>
            <p className="text-gray-300 mt-1">
              Kerala Bullion - <span className="text-[#C9A967]">AKGSMA S-BHAVAN</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <span className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">
                <Wifi className="h-4 w-4" /> Connected
              </span>
            ) : (
              <span className="flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">
                <WifiOff className="h-4 w-4" /> Offline
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Live Rates Display */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200">
          <p className="text-sm font-medium text-yellow-700">22K Gold</p>
          <p className="text-3xl font-bold text-yellow-800 mt-1">
            {formatRate(rates.gold_22k?.rate_per_gram)}
          </p>
          <p className="text-xs text-yellow-600 mt-1">per gram</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200">
          <p className="text-sm font-medium text-yellow-700">18K Gold</p>
          <p className="text-3xl font-bold text-yellow-800 mt-1">
            {formatRate(rates.gold_18k?.rate_per_gram)}
          </p>
          <p className="text-xs text-yellow-600 mt-1">per gram</p>
        </div>
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
          <p className="text-sm font-medium text-gray-600">999 Silver</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">
            {formatRate(rates.silver_999?.rate_per_gram)}
          </p>
          <p className="text-xs text-gray-500 mt-1">per gram</p>
        </div>
      </div>

      {/* Method Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-lg text-[#1a3a5c] mb-4">Rate Update Method</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Option 0: Manual Entry - RECOMMENDED */}
          <div 
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              activeMethod === 'manual' 
                ? 'border-[#C9A967] bg-[#C9A967]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMethodChange('manual')}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                activeMethod === 'manual' ? 'bg-[#C9A967]' : 'bg-gray-200'
              }`}>
                <Edit3 className={`h-5 w-5 ${activeMethod === 'manual' ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">Quick Entry</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Enter rates manually from WhatsApp message (10 seconds)
                </p>
                <span className="inline-flex items-center gap-1 mt-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <CheckCircle className="h-3 w-3" /> Recommended
                </span>
              </div>
            </div>
          </div>

          {/* Option 1: Android/MacroDroid */}
          <div 
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              activeMethod === 'webhook' 
                ? 'border-[#C9A967] bg-[#C9A967]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMethodChange('webhook')}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                activeMethod === 'webhook' ? 'bg-[#C9A967]' : 'bg-gray-200'
              }`}>
                <Smartphone className={`h-5 w-5 ${activeMethod === 'webhook' ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">Android Auto</h4>
                <p className="text-sm text-gray-500 mt-1">
                  MacroDroid automation (may not work on all phones)
                </p>
                {hasWebhookRates && (
                  <span className="inline-flex items-center gap-1 mt-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    <CheckCircle className="h-3 w-3" /> Receiving rates
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Option 2: Local Computer */}
          <div 
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              activeMethod === 'server' 
                ? 'border-[#C9A967] bg-[#C9A967]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMethodChange('server')}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                activeMethod === 'server' ? 'bg-[#C9A967]' : 'bg-gray-200'
              }`}>
                <Monitor className={`h-5 w-5 ${activeMethod === 'server' ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">Local Computer</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Python script for automatic fetching every 30 min
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Entry Form */}
      {activeMethod === 'manual' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="font-semibold text-lg text-[#1a3a5c] mb-4 flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-[#C9A967]" />
            Quick Rate Entry
          </h4>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>How to use:</strong> When you receive a rate message from keralabullion on WhatsApp, 
              just enter the rates below. Only 22K is required - 18K will be calculated automatically if left empty.
            </p>
          </div>
          
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  22K Gold (per gram) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={manualRates.gold_22k}
                  onChange={(e) => setManualRates({...manualRates, gold_22k: e.target.value})}
                  placeholder="e.g., 14885"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A967] focus:border-[#C9A967]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  18K Gold (per gram)
                </label>
                <input
                  type="number"
                  value={manualRates.gold_18k}
                  onChange={(e) => setManualRates({...manualRates, gold_18k: e.target.value})}
                  placeholder="Auto-calculated if empty"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A967] focus:border-[#C9A967]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Silver 995 (per gram)
                </label>
                <input
                  type="number"
                  value={manualRates.silver_999}
                  onChange={(e) => setManualRates({...manualRates, silver_999: e.target.value})}
                  placeholder="e.g., 290"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A967] focus:border-[#C9A967]"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={submittingManual}
                className="px-6 py-2 bg-[#C9A967] text-white font-medium rounded-lg hover:bg-[#b8985a] disabled:opacity-50 flex items-center gap-2"
              >
                {submittingManual ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Update Rates
                  </>
                )}
              </button>
              
              {manualRates.gold_22k && !manualRates.gold_18k && (
                <span className="text-sm text-gray-500">
                  18K will be set to: ₹{Math.round(parseFloat(manualRates.gold_22k) * 18 / 22).toLocaleString('en-IN')}
                </span>
              )}
            </div>
          </form>
          
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h5 className="font-medium text-gray-700 mb-2">WhatsApp Message Format Reference:</h5>
            <div className="bg-gray-50 rounded-lg p-3 text-sm font-mono text-gray-600">
              AKGSMA S-BHAVAN<br/>
              1gm:<span className="text-green-600 font-bold">14885</span> / 8gms:119080 ← 22K<br/>
              <br/>
              (18K/750)<br/>
              1gm:<span className="text-green-600 font-bold">12285</span> / 8gms:98280 ← 18K<br/>
              <br/>
              SILVER RATE 995: <span className="text-green-600 font-bold">290</span> ← Silver
            </div>
          </div>
        </div>
      )}

      {/* Option 1: Android Setup */}
      {activeMethod === 'webhook' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowAndroidSetup(!showAndroidSetup)}
            className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100"
          >
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-800">WhatsApp + MacroDroid Setup Guide (Recommended)</span>
            </div>
            {showAndroidSetup ? <ChevronUp className="h-5 w-5 text-green-600" /> : <ChevronDown className="h-5 w-5 text-green-600" />}
          </button>
          
          {showAndroidSetup && (
            <div className="p-6 space-y-6">
              {/* Step 0: Test Connection */}
              <div className="bg-orange-50 border-2 border-orange-300 p-4 rounded-lg">
                <h4 className="font-bold text-orange-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Step 0: Test Connection First!
                </h4>
                <p className="text-sm text-orange-700 mb-2">
                  Open this URL in your <strong>phone browser</strong> to verify the server is reachable:
                </p>
                <div className="bg-white rounded p-2 flex items-center gap-2">
                  <code className="text-xs text-orange-900 break-all flex-1">{`${window.location.origin}/api/kerala-bullion/webhook/debug`}</code>
                  <button onClick={() => copyToClipboard(`${window.location.origin}/api/kerala-bullion/webhook/debug`)} className="text-[#C9A967] hover:text-[#b8985a] flex-shrink-0">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-orange-600 mt-2">
                  If you see a JSON response, your phone can reach the server. If not, check your internet connection.
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Prerequisites</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>1. Android phone with WhatsApp</li>
                  <li>2. You already receive rate updates from keralabullion on WhatsApp</li>
                  <li>3. Install <strong>MacroDroid</strong> from Play Store (Free)</li>
                  <li>4. Grant MacroDroid <strong>Notification Access</strong> (Settings → Notification Access → MacroDroid → Enable)</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-gray-800 mb-3">MacroDroid Setup:</h4>
                <div className="space-y-4">
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 bg-[#C9A967] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                    <div>
                      <p className="font-medium">Trigger: Notification Received</p>
                      <ul className="text-sm text-gray-500 mt-1 space-y-0.5">
                        <li>- Select App: <strong>WhatsApp</strong></li>
                        <li>- Text Content: Contains <strong>"AKGSMA"</strong></li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 bg-[#C9A967] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                    <div>
                      <p className="font-medium">Action: HTTP Request</p>
                      <ul className="text-sm text-gray-500 mt-1 space-y-0.5">
                        <li>- Method: <strong>POST</strong></li>
                        <li>- URL: <em>(copy from below)</em></li>
                        <li>- Content Type: <strong>application/json</strong></li>
                        <li>- HTTP Body: <em>(copy from below)</em></li>
                        <li>- <strong>Block cookies:</strong> ON</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">URL (copy exactly):</span>
                  <button onClick={() => copyToClipboard(webhookUrl)} className="text-[#C9A967] hover:text-[#b8985a]">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <code className="text-green-400 text-sm break-all">{webhookUrl}</code>
              </div>

              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">HTTP Body (copy exactly):</span>
                  <button onClick={() => copyToClipboard(`{"notification_text": "{notification}", "secret_key": "${secretKey}"}`)} className="text-[#C9A967] hover:text-[#b8985a]">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <code className="text-green-400 text-sm">
                  {`{"notification_text": "{notification}", "secret_key": "${secretKey}"}`}
                </code>
              </div>

              <div className="bg-red-50 border-2 border-red-300 p-4 rounded-lg">
                <h4 className="font-bold text-red-800 mb-2">Important MacroDroid Settings</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>1. In HTTP Request action, set <strong>Content Type = application/json</strong></li>
                  <li>2. Make sure <strong>{"{notification}"}</strong> is typed exactly (with curly braces)</li>
                  <li>3. Do NOT add any extra headers unless needed</li>
                  <li>4. Test: Go to MacroDroid → Your Macro → <strong>Test Actions</strong> to send a test request</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Troubleshooting</h4>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li><strong>Popup shows but webhook not received?</strong>
                    <br/>→ First test with debug URL above. Then in MacroDroid, use "Test Actions" and check Webhook History below.
                  </li>
                  <li><strong>Webhook shows "Invalid secret key"?</strong>
                    <br/>→ Re-copy the HTTP Body from above. Make sure no extra spaces.
                  </li>
                  <li><strong>Webhook shows "Could not find 22K gold rate"?</strong>
                    <br/>→ The notification text was received but couldn't parse rates. Check Webhook History for the raw text.
                  </li>
                  <li><strong>Nothing in Webhook History?</strong>
                    <br/>→ MacroDroid HTTP request is not reaching the server. Check: internet, URL copied correctly, POST method selected.
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Option 2: Local Computer Setup */}
      {activeMethod === 'server' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowComputerSetup(!showComputerSetup)}
            className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100"
          >
            <div className="flex items-center gap-3">
              <Monitor className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-800">Local Computer Setup Guide</span>
            </div>
            {showComputerSetup ? <ChevronUp className="h-5 w-5 text-blue-600" /> : <ChevronDown className="h-5 w-5 text-blue-600" />}
          </button>
          
          {showComputerSetup && (
            <div className="p-6 space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Prerequisites</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Python 3.8 or higher</li>
                  <li>• Install: <code className="bg-blue-100 px-1 rounded">pip install playwright requests</code></li>
                  <li>• Install browser: <code className="bg-blue-100 px-1 rounded">playwright install chromium</code></li>
                </ul>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-800">Python Script:</h4>
                  <button
                    onClick={() => {
                      const blob = new Blob([localComputerScript], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'kerala_bullion_pusher.py';
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Script downloaded!');
                    }}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download Script
                  </button>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 max-h-64 overflow-y-auto">
                  <pre className="text-green-400 text-xs whitespace-pre-wrap">{localComputerScript.slice(0, 1500)}...</pre>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">How to Run:</h4>
                <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
                  <li>Download and save the script as <code className="bg-yellow-100 px-1 rounded">kerala_bullion_pusher.py</code></li>
                  <li>Open terminal/command prompt</li>
                  <li>Run: <code className="bg-yellow-100 px-1 rounded">python kerala_bullion_pusher.py</code></li>
                  <li>Keep the terminal open - it will auto-fetch every 30 minutes during trading hours</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Webhook History */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-gray-600" />
            <span className="font-medium text-gray-800">Webhook History</span>
            {webhookHistory.length > 0 && (
              <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">{webhookHistory.length}</span>
            )}
          </div>
          {showHistory ? <ChevronUp className="h-5 w-5 text-gray-600" /> : <ChevronDown className="h-5 w-5 text-gray-600" />}
        </button>
        
        {showHistory && (
          <div className="p-4 border-t border-gray-100 max-h-64 overflow-y-auto">
            {webhookHistory.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">No webhook calls yet</p>
            ) : (
              <div className="space-y-2">
                {webhookHistory.map((entry, idx) => (
                  <div key={`wh-${entry.timestamp}-${idx}`} className={`p-3 rounded-lg text-sm ${entry.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${entry.success ? 'text-green-700' : 'text-red-700'}`}>
                        {entry.success ? '✓ Success' : '✗ Failed'}
                      </span>
                      <span className="text-gray-500 text-xs">{formatTime(entry.timestamp)}</span>
                    </div>
                    {entry.success && entry.rates && (
                      <p className="text-green-600 mt-1">
                        22K: ₹{entry.rates.gold_22k} | 18K: ₹{entry.rates.gold_18k} | Silver: ₹{entry.rates.silver_999 || '—'}
                      </p>
                    )}
                    {entry.found_from_screen?.length > 0 && (
                      <p className="text-green-600 text-xs mt-1">From app: {entry.found_from_screen.join(', ')}</p>
                    )}
                    {!entry.success && <p className="text-red-600 mt-1">{entry.error}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Parser Config */}
      <ParserConfigPanel />

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-[#1a3a5c] mb-4">Sync to Product Pricing</h3>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoSync}
                onChange={async (e) => {
                  const enabled = e.target.checked;
                  setAutoSync(enabled);
                  try {
                    await api.put('/kerala-bullion/auto-sync-setting', { enabled });
                    toast.success(enabled ? 'Auto-sync enabled — rates will sync automatically when webhook receives new rates' : 'Auto-sync disabled');
                  } catch (err) {
                    setAutoSync(!enabled);
                    toast.error('Failed to update auto-sync setting');
                  }
                }}
                className="w-5 h-5 rounded border-gray-300 text-[#C9A967] focus:ring-[#C9A967]"
              />
              <span className="text-gray-700">Auto-sync when rates update</span>
            </label>
            <button
              onClick={() => handleSyncToRates(false)}
              disabled={syncing || !rates.gold_22k?.rate_per_gram}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#C9A967] text-white rounded-lg hover:bg-[#b8985a] disabled:opacity-50"
            >
              <ArrowRight className="h-5 w-5" />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-[#1a3a5c] mb-4">Status</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Last Update:</span>
              <span className="text-gray-800">{formatTime(liveRates?.timestamp || webhookStatus?.timestamp)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Source:</span>
              <span className="text-gray-800">{hasWebhookRates ? 'Webhook (Phone/Computer)' : 'Server Scraper'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Webhook Calls:</span>
              <span className="text-gray-800">{webhookHistory.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
